/**
 * Equipment Page
 * BuilderBel Construction Project Management System
 *
 * Lists all equipment for the current project with filtering, stats,
 * workflow actions (assign, return, maintenance), and PDF download.
 */

import {
  ChevronLeft,
  ChevronRight,
  Download,
  Eye,
  Pencil,
  Plus,
  Trash2,
  Truck,
  Wrench,
} from 'lucide-react';
import React, { useCallback, useMemo, useState } from 'react';
import { SearchableDropdown } from '../components/forms';
import { EquipmentDrawer } from '../components/equipment';
import LoadingSpinner from '../components/LoadingSpinner';
import {
  ActionMenu,
  type ActionMenuItem,
  EmptyState,
  ModuleLayout,
  StatusBadge,
} from '../components/ui';
import { useToast } from '../contexts/ToastContext';
import { useCurrentProjectId } from '../hooks/useProjectRedux';
import {
  useDeleteEquipmentMutation,
  useGetEquipmentQuery,
  useGetEquipmentStatsQuery,
} from '../services/equipmentApi';
import type {
  Equipment,
  EquipmentListParams,
  EquipmentStatus,
  EquipmentCategory,
  EquipmentOwnership,
} from '../types/equipment';

// ============================================================================
// Constants
// ============================================================================

const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'All Status' },
  { value: 'available', label: 'Available' },
  { value: 'assigned', label: 'Assigned' },
  { value: 'in_maintenance', label: 'In Maintenance' },
  { value: 'out_of_service', label: 'Out of Service' },
  { value: 'retired', label: 'Retired' },
];

const CATEGORY_FILTER_OPTIONS = [
  { value: '', label: 'All Categories' },
  { value: 'heavy', label: 'Heavy Equipment' },
  { value: 'light', label: 'Light Equipment' },
  { value: 'vehicle', label: 'Vehicle' },
  { value: 'tool', label: 'Tool' },
  { value: 'safety', label: 'Safety Equipment' },
  { value: 'other', label: 'Other' },
];

const OWNERSHIP_FILTER_OPTIONS = [
  { value: '', label: 'All Ownership' },
  { value: 'owned', label: 'Owned' },
  { value: 'rented', label: 'Rented' },
  { value: 'leased', label: 'Leased' },
];

const SORT_OPTIONS = [
  { value: 'createdAt', label: 'Date Added' },
  { value: 'name', label: 'Name' },
  { value: 'status', label: 'Status' },
  { value: 'category', label: 'Category' },
  { value: 'equipmentNumber', label: 'Equipment Number' },
];

const PAGE_SIZE = 20;

// ============================================================================
// Helpers
// ============================================================================

const getStatusVariant = (
  status: EquipmentStatus
): 'success' | 'warning' | 'error' | 'info' | 'neutral' => {
  switch (status) {
    case 'available':
      return 'success';
    case 'assigned':
      return 'info';
    case 'in_maintenance':
      return 'warning';
    case 'out_of_service':
      return 'error';
    case 'retired':
    default:
      return 'neutral';
  }
};

const formatDate = (dateStr?: string): string => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatCurrency = (amount?: number): string => {
  if (amount === undefined || amount === null) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
  }).format(amount);
};

// ============================================================================
// Main Component
// ============================================================================

const EquipmentPage: React.FC = () => {
  const projectId = useCurrentProjectId();
  const toast = useToast();

  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [ownershipFilter, setOwnershipFilter] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState(1);

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedEquipment, setSelectedEquipment] = useState<Equipment | null>(null);
  const [drawerMode, setDrawerMode] = useState<'create' | 'edit'>('create');

  // Build query params
  const queryParams = useMemo<EquipmentListParams>(() => {
    const params: EquipmentListParams = {
      page: currentPage,
      limit: PAGE_SIZE,
      sortBy: sortBy as EquipmentListParams['sortBy'],
      sortOrder,
    };
    if (statusFilter) params.status = statusFilter as EquipmentStatus;
    if (categoryFilter) params.category = categoryFilter as EquipmentCategory;
    if (ownershipFilter) params.ownership = ownershipFilter as EquipmentOwnership;
    return params;
  }, [currentPage, sortBy, sortOrder, statusFilter, categoryFilter, ownershipFilter]);

  // RTK Query
  const { data: equipmentData, isLoading } = useGetEquipmentQuery(
    { projectId: projectId || '', params: queryParams },
    { skip: !projectId }
  );

  const { data: statsData } = useGetEquipmentStatsQuery(
    { projectId: projectId || '' },
    { skip: !projectId }
  );

  const [deleteEquipment, { isLoading: isDeleting }] = useDeleteEquipmentMutation();

  const equipmentList = equipmentData?.data?.equipment ?? [];
  const pagination = equipmentData?.data?.pagination;
  const stats = statsData?.data;

  // ============================================================================
  // Handlers
  // ============================================================================

  const handleOpenCreate = useCallback(() => {
    setSelectedEquipment(null);
    setDrawerMode('create');
    setDrawerOpen(true);
  }, []);

  const handleOpenEdit = useCallback((eq: Equipment) => {
    setSelectedEquipment(eq);
    setDrawerMode('edit');
    setDrawerOpen(true);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setDrawerOpen(false);
    setSelectedEquipment(null);
  }, []);

  const handleDelete = useCallback(
    async (eq: Equipment) => {
      if (!confirm(`Delete "${eq.name}"? This cannot be undone.`)) return;
      try {
        await deleteEquipment({
          projectId: projectId || '',
          equipmentId: eq._id,
        }).unwrap();
        toast.success('Equipment deleted');
      } catch {
        toast.error('Failed to delete equipment');
      }
    },
    [deleteEquipment, projectId, toast]
  );

  const handleDownloadPDF = useCallback(
    async (eq: Equipment) => {
      try {
        const token = localStorage.getItem('auth_token');
        const url = `/api/projects/${projectId}/equipment/${eq._id}/pdf`;
        const response = await fetch(url, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!response.ok) {
          toast.error('Failed to generate PDF');
          return;
        }
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = `equipment-${eq.equipmentNumber}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      } catch {
        toast.error('Failed to download PDF');
      }
    },
    [projectId, toast]
  );

  // ============================================================================
  // Action menu builder
  // ============================================================================

  const getActions = useCallback(
    (eq: Equipment): ActionMenuItem[] => {
      const isRetired = eq.status === 'retired';
      const actions: ActionMenuItem[] = [
        {
          label: 'View / Edit',
          icon: <Eye className="h-4 w-4" />,
          onClick: () => handleOpenEdit(eq),
        },
      ];

      if (!isRetired) {
        actions.push({
          label: 'Edit',
          icon: <Pencil className="h-4 w-4" />,
          onClick: () => handleOpenEdit(eq),
        });
      }

      actions.push({
        label: 'Download PDF',
        icon: <Download className="h-4 w-4" />,
        onClick: () => handleDownloadPDF(eq),
      });

      if (!isRetired && eq.status !== 'assigned' && eq.status !== 'in_maintenance') {
        actions.push({
          label: 'Delete',
          icon: <Trash2 className="h-4 w-4" />,
          onClick: () => handleDelete(eq),
          variant: 'danger',
        });
      }

      return actions;
    },
    [handleOpenEdit, handleDownloadPDF, handleDelete]
  );

  // ============================================================================
  // Stats cards
  // ============================================================================

  const moduleStats = useMemo(() => {
    if (!stats) return [];
    return [
      { title: 'Total', value: stats.total },
      { title: 'Available', value: stats.available },
      { title: 'Assigned', value: stats.assigned },
      { title: 'Maintenance Due', value: stats.maintenanceDue },
    ];
  }, [stats]);

  // ============================================================================
  // Render
  // ============================================================================

  const isActionLoading = isDeleting;

  return (
    <ModuleLayout
      title="Equipment"
      description="Track construction equipment, assignments, and maintenance schedules"
      icon={<Truck className="h-4 w-4 text-brand-600" />}
      primaryAction={{
        label: 'Add Equipment',
        onClick: handleOpenCreate,
        icon: <Plus className="h-4 w-4 mr-2" />,
      }}
      stats={moduleStats}
    >
      {/* ======================================================================
          FILTERS
      ====================================================================== */}
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div className="w-40">
          <SearchableDropdown
            options={STATUS_FILTER_OPTIONS}
            value={statusFilter}
            onChange={val => {
              setStatusFilter(val);
              setCurrentPage(1);
            }}
            placeholder="All Status"
          />
        </div>
        <div className="w-44">
          <SearchableDropdown
            options={CATEGORY_FILTER_OPTIONS}
            value={categoryFilter}
            onChange={val => {
              setCategoryFilter(val);
              setCurrentPage(1);
            }}
            placeholder="All Categories"
          />
        </div>
        <div className="w-40">
          <SearchableDropdown
            options={OWNERSHIP_FILTER_OPTIONS}
            value={ownershipFilter}
            onChange={val => {
              setOwnershipFilter(val);
              setCurrentPage(1);
            }}
            placeholder="All Ownership"
          />
        </div>
        <div className="w-44">
          <SearchableDropdown
            options={SORT_OPTIONS}
            value={sortBy}
            onChange={val => {
              setSortBy(val);
              setCurrentPage(1);
            }}
            placeholder="Sort by"
          />
        </div>
      </div>

      {/* ======================================================================
          TABLE
      ====================================================================== */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <LoadingSpinner />
        </div>
      ) : equipmentList.length === 0 ? (
        <EmptyState
          icon={Truck}
          title="No equipment found"
          description="Track equipment location, maintenance schedules, and availability for this project."
          action={{
            label: 'Add Equipment',
            onClick: handleOpenCreate,
          }}
        />
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Equipment
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category / Type
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ownership
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Hours / Odometer
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Rate
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Next Maint.
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {equipmentList.map(eq => (
                  <tr
                    key={eq._id}
                    className={`hover:bg-gray-50 transition-colors ${isActionLoading ? 'opacity-60 pointer-events-none' : ''}`}
                  >
                    {/* Equipment name + number */}
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-sm font-medium text-gray-900 truncate max-w-[200px]">
                          {eq.name}
                        </p>
                        <p className="text-xs text-gray-400 mt-0.5">{eq.equipmentNumber}</p>
                        {(eq.make || eq.modelName) && (
                          <p className="text-xs text-gray-400">
                            {eq.make} {eq.modelName}
                            {eq.year ? ` (${eq.year})` : ''}
                          </p>
                        )}
                      </div>
                    </td>

                    {/* Category / type */}
                    <td className="px-4 py-3">
                      <p className="text-sm text-gray-700 capitalize">
                        {eq.category.replace(/_/g, ' ')}
                      </p>
                      <p className="text-xs text-gray-400">{eq.type}</p>
                    </td>

                    {/* Status */}
                    <td className="px-4 py-3">
                      <StatusBadge
                        status={eq.status.replace(/_/g, ' ')}
                        variant={getStatusVariant(eq.status)}
                      />
                      {eq.currentAssignment && (
                        <p className="text-xs text-gray-400 mt-0.5 truncate max-w-[140px]">
                          {eq.currentAssignment.projectName}
                        </p>
                      )}
                    </td>

                    {/* Ownership */}
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-600 capitalize">{eq.ownership}</span>
                    </td>

                    {/* Meters */}
                    <td className="px-4 py-3">
                      <div className="text-xs text-gray-600">
                        {eq.hourMeter !== undefined ? (
                          <span>{eq.hourMeter.toLocaleString()} hrs</span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                        {eq.odometerReading !== undefined && (
                          <p className="text-gray-400">
                            {eq.odometerReading.toLocaleString()} mi
                          </p>
                        )}
                      </div>
                    </td>

                    {/* Hourly rate */}
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-600">
                        {eq.hourlyRate !== undefined
                          ? `${formatCurrency(eq.hourlyRate)}/hr`
                          : '—'}
                      </span>
                    </td>

                    {/* Next maintenance */}
                    <td className="px-4 py-3">
                      {eq.nextMaintenanceDate ? (
                        <div>
                          <span
                            className={`text-xs ${
                              new Date(eq.nextMaintenanceDate) <= new Date()
                                ? 'text-red-600 font-medium'
                                : 'text-gray-600'
                            }`}
                          >
                            {formatDate(eq.nextMaintenanceDate)}
                          </span>
                          {new Date(eq.nextMaintenanceDate) <= new Date() && (
                            <span className="ml-1 text-xs text-red-500">
                              <Wrench className="h-3 w-3 inline" /> Overdue
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">—</span>
                      )}
                    </td>

                    {/* Actions */}
                    <td className="px-4 py-3 text-right">
                      <ActionMenu
                        items={getActions(eq)}
                        id={`action-menu-${eq._id}`}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination && pagination.pages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 bg-gray-50">
              <p className="text-xs text-gray-500">
                Showing {(pagination.page - 1) * pagination.limit + 1}–
                {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                {pagination.total} items
              </p>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setCurrentPage(p => p - 1)}
                  disabled={!pagination.hasPrev}
                  className="p-1.5 rounded-lg border border-gray-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-white"
                >
                  <ChevronLeft className="h-4 w-4 text-gray-600" />
                </button>
                <span className="text-xs text-gray-600 px-2">
                  Page {pagination.page} of {pagination.pages}
                </span>
                <button
                  onClick={() => setCurrentPage(p => p + 1)}
                  disabled={!pagination.hasNext}
                  className="p-1.5 rounded-lg border border-gray-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-white"
                >
                  <ChevronRight className="h-4 w-4 text-gray-600" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ======================================================================
          DRAWER
      ====================================================================== */}
      <EquipmentDrawer
        isOpen={drawerOpen}
        onClose={handleCloseDrawer}
        projectId={projectId || ''}
        equipment={selectedEquipment}
        mode={drawerMode}
      />
    </ModuleLayout>
  );
};

export default EquipmentPage;
